// eslint-disable-next-line no-unused-vars
import { _getInstance, StatsigClient, StatsigEvent } from '@statsig/js-client'
import Config from 'Common/Config/Config'
import { AnalyticsEventTypes, AnalyticsPageCategories } from './constants'
import {
  getDeviceId,
  getGeoCountryCode,
  getSessionUtmParameters
} from './utilities'
import { ConsentStatuses } from 'Common/Service/Consent/constants'
// eslint-disable-next-line no-unused-vars
import { IAnalyticsService } from './types'

/**
 * @returns {StatsigEvent}
 */
function convertToStatsigEvent (event) {
  const now = new Date()
  const utmParameters = getSessionUtmParameters()
  const metadata = Object.entries(
    event.data === undefined || event.data === null ? {} : event.data
  ).reduce(
    (metadata, [key, value]) => {
      if (value === undefined || value === null) {
        return metadata
      }

      return {
        ...metadata,
        [key]: Array.isArray(value) ? value.join(', ') : value.toString()
      }
    },
    {
      countryCode: getGeoCountryCode(),
      deviceId: getDeviceId(),
      locale: window.document.documentElement.lang,
      partitionDate: `${now.getUTCFullYear()}-${(now.getUTCMonth() + 1)
        .toString()
        .padStart(2, '0')}-${now
        .getUTCDate()
        .toString()
        .padStart(2, '0')}`,
      partitionHour: now
        .getUTCHours()
        .toString()
        .padStart(2, '0'),
      sport: 'lol',
      timestamp: now.toISOString(),
      url: window.location.href,
      utm_campaign: utmParameters?.campaign,
      utm_content: utmParameters?.content,
      utm_medium: utmParameters?.medium,
      utm_source: utmParameters?.source,
      utm_term: utmParameters?.term
    }
  )

  if (event.type !== AnalyticsEventTypes.PageView) {
    return {
      eventName: event.type,
      metadata
    }
  }

  return {
    eventName: event.type,
    metadata: {
      ...metadata,
      category: AnalyticsPageCategories[event.data.name],
      screenHeight: window.screen.height.toString(),
      screenWidth: window.screen.width.toString(),
      timestamp: new Date().toISOString(),
      userAgent: window.navigator.userAgent
    },
    value: event.data.name
  }
}

/**
 * @implements {IAnalyticsService}
 */
export class StatsigAnalyticsService {
  #client

  #consentManagementService

  /**
   * @param {import('../Consent/types').IConsentManagementService} consentManagementService
   */
  constructor (consentManagementService) {
    this.#consentManagementService = consentManagementService

    const existingInstance = _getInstance(
      process.env.ANALYTICS__STATSIG_CLIENT_KEY
    )

    if (existingInstance instanceof StatsigClient) {
      this.#client = existingInstance
      return
    }

    this.#client = new StatsigClient(
      process.env.ANALYTICS__STATSIG_CLIENT_KEY,
      {},
      {
        environment: {
          tier: Config.getEnvironment()
        }
      }
    )
  }

  reset () {
    const existingUserId = this.#client.getContextHandle().user.userID

    if (!existingUserId) {
      if (this.#client.loadingStatus === 'Ready') {
        return Promise.resolve()
      }

      return this.#client.initializeAsync().then(() => {})
    }

    return this.#client.updateUserAsync({ userID: undefined }).then(() => {})
  }

  setProfile (puuid) {
    const existingUserId = this.#client.getContextHandle().user.userID

    if (existingUserId === puuid) {
      if (this.#client.loadingStatus === 'Ready') {
        return Promise.resolve()
      }

      return this.#client.initializeAsync().then(() => {})
    }

    return this.#client
      .updateUserAsync({
        userID: puuid
      })
      .then(() => {})
  }

  shutdown () {
    if (
      this.#consentManagementService.getConsent().analytics
      !== ConsentStatuses.Accept
    ) {
      return Promise.resolve()
    }

    // We're intentionally using `client.flush()` and not `client.shutdown()` since we're likely
    // relying on the `visibilitychange` event as it's the most reliable way to determine if the
    // browser/tab is closing. However, it also fires when the browser is minimized or when the
    // tab loses focus. `client.shutdown()` would be problematic because it would reset other
    // properties in the SDK.
    return this.#client.flush()
  }

  track (type, data) {
    if (
      this.#consentManagementService.getConsent().analytics
      !== ConsentStatuses.Accept
    ) {
      return
    }

    const statsigEvent = convertToStatsigEvent({
      data,
      type
    })

    this.#client.logEvent(statsigEvent)
  }
}
